import styled from 'styled-components'

import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { IconCheck } from 'components/Icons/IconCheck'
import { IconInfo } from 'components/Icons/InfoIcon'
import { Menu } from 'components/Menu/Menu'
import { Typography } from 'components/Typography/Typography'
import { useCallback, useRef, useState } from 'react'
import { useOnClickOutside } from 'utils/onClickOutside'
import { useIsMobile } from 'utils/useIsMobile'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { ROUTER_OPTIONS, SwapRouter, useRouter } from 'contexts/SwapRouterContext'

const SwapAggregator = styled(Typography)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const MenuTrigger = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 16px;
  border-radius: 8px;
  border: 0.7px solid ${p => p.theme.colors.divider};
  cursor: pointer;
  div {
    display: flex;
    align-items: center;
  }
`

const StyledMenuItem = styled.div`
  & > div {
    padding: 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: ${p => p.theme.colors.background.hover};
    }
  }
`

interface MenuItemProps {
  item: SwapRouter
  activeOption: SwapRouter
  onClick: (o: SwapRouter) => void
}

const getRouterImgUrl = (router: SwapRouter) => {
  switch (router) {
    case SwapRouter.Cetus:
      return '/cetus.png'
    case SwapRouter.Aftermath:
      return '/aftermath.png'
    default:
      return undefined
  }
}

const MenuItem = ({ item, onClick, activeOption }: MenuItemProps) => {
  const isMobile = useIsMobile()
  const imageUrl = getRouterImgUrl(item)

  return (
    <StyledMenuItem onClick={() => onClick(item)}>
      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
        {imageUrl ? <img src={imageUrl} style={{ width: 24, height: 24, marginRight: 8 }} /> : null}
        {item}
        {item === activeOption && (
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            <IconCheck />
          </div>
        )}
      </Typography>
    </StyledMenuItem>
  )
}

export const SelectSwapAggregatorMenu = () => {
  const isMobile = useIsMobile()

  const { onRouterChange, routerLabel } = useRouter()

  const [isMenuOpen, setMenuOpen] = useState(false)

  const menuNode = useRef<HTMLDivElement>(null)

  const onClose = useCallback(() => setMenuOpen(false), [])

  useOnClickOutside(menuNode, onClose)

  const toggleMenu = useCallback(() => setMenuOpen(!isMenuOpen), [isMenuOpen])

  const handleOptionClick = useCallback(
    (router: SwapRouter) => {
      onRouterChange(router)
      toggleMenu()
    },
    [onRouterChange, toggleMenu]
  )

  const imageUrl = getRouterImgUrl(routerLabel)

  return (
    <div style={{ position: 'relative', marginBottom: 24 }}>
      <SwapAggregator variant="label2" color="secondary" fontWeight="medium">
        Choose swap aggregator
        <Tooltip content="Select a platform to handle your swap routing." position="top">
          <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
        </Tooltip>
      </SwapAggregator>
      <MenuTrigger onClick={toggleMenu}>
        <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="bold">
          {imageUrl ? <img src={imageUrl} style={{ width: 24, height: 24, marginRight: 8 }} /> : null}
          {routerLabel}
        </Typography>
        <div>
          <IconArrowDown />
        </div>
      </MenuTrigger>
      {isMenuOpen && (
        <Menu anchorElement={menuNode} style={{ top: 112, width: '100%', padding: '8px 0', zIndex: 1 }}>
          {ROUTER_OPTIONS.map(o => (
            <MenuItem key={o} item={o} onClick={handleOptionClick} activeOption={routerLabel} />
          ))}
        </Menu>
      )}
    </div>
  )
}
